/**
 * Cumpără clipuri (App-Cumpara.dc.html) — catalog live cu countdown reduceri, sumar
 * comandă (reducere, TVA inclus, total), profil facturare Firmă/PF, promo, „Continuă la
 * plată" → Stripe. În dev/e2e plata se simulează prin endpoint-ul dev.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProductCategory, ProductTier } from '@reclame-ai/shared';
import { useBillingProfiles, useCatalog, useMutation, useInvalidate, qk } from '../api/hooks';
import { createOrder, devSimulatePayment, validatePromo } from '../api/endpoints';
import { humanError } from '../api/client';
import { useToast } from '../context/ToastContext';
import { categoryLabel, counterLabel, formatCountdown, formatRon, tierLabel } from '../lib/format';
import type { CatalogSku, Order, PromoValidation } from '../api/types';
import { Segmented, Spinner } from '../components/ui';
import { BillingProfiles } from '../components/BillingProfiles';

function useNow(active: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const t = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(t);
  }, [active]);
}

export function CumparaPage() {
  const catalog = useCatalog();
  const billing = useBillingProfiles();
  const invalidate = useInvalidate();
  const toast = useToast();

  const [category, setCategory] = useState<ProductCategory>('organic');
  const [tier, setTier] = useState<ProductTier>('premium');
  const [selectedSku, setSelectedSku] = useState<string | null>(null);
  const [billingProfileId, setBillingProfileId] = useState<string | null>(null);
  const [promo, setPromo] = useState('');
  const [promoResult, setPromoResult] = useState<PromoValidation | null>(null);
  const [paidOrder, setPaidOrder] = useState<Order | null>(null);
  const [checkout, setCheckout] = useState<{ order: Order; mock: boolean } | null>(null);

  useNow(true);
  useEffect(() => {
    document.title = 'Cumpără clipuri — Reclame Video';
  }, []);

  const items = useMemo(
    () => (catalog.data?.items ?? []).filter((s) => s.category === category && s.tier === tier).sort((a, b) => a.clips - b.clips),
    [catalog.data, category, tier],
  );
  const selected = items.find((s) => s.id === selectedSku) ?? null;

  useEffect(() => {
    // Selecție implicită: pachetul recomandat sau primul.
    if (items.length && (!selected || !items.some((s) => s.id === selectedSku))) {
      setSelectedSku((items.find((s) => s.recommended) ?? items[0])!.id);
      setPromoResult(null);
    }
  }, [items]);

  const promoMut = useMutation({
    mutationFn: () => validatePromo({ skuId: selectedSku!, code: promo.trim() }),
    onSuccess: (res) => {
      setPromoResult(res);
      toast.push({ kind: 'ok', title: 'Cod promo aplicat' });
    },
    onError: (err) => {
      setPromoResult({ valid: false, message: humanError(err) });
      toast.push({ kind: 'warn', title: 'Cod promo invalid', body: humanError(err) });
    },
  });

  const orderMut = useMutation({
    mutationFn: () =>
      createOrder({ skuId: selectedSku!, promoCode: promoResult?.valid && promo ? promo.trim() : undefined, billingProfileId: billingProfileId ?? undefined }),
    onSuccess: ({ order, checkoutUrl }) => {
      const mock = Boolean(checkoutUrl && checkoutUrl.includes('checkout-mock'));
      if (!mock && checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      setCheckout({ order, mock: true });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut crea comanda', body: humanError(err) }),
  });

  const payMut = useMutation({
    mutationFn: (orderId: string) => devSimulatePayment(orderId),
    onSuccess: () => {
      invalidate([qk.balance(), qk.orders()]);
      if (checkout) setPaidOrder(checkout.order);
      setCheckout(null);
      toast.push({ kind: 'ok', title: 'Plata a reușit!' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Plata a eșuat', body: humanError(err) }),
  });

  // ---- Ecran de succes ----
  if (paidOrder) {
    return (
      <div style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center', paddingTop: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--ok-soft)', color: 'var(--ok)', display: 'grid', placeItems: 'center', fontSize: 30, margin: '0 auto 14px' }}>✓</div>
        <h1 className="bricolage" data-testid="payment-success" style={{ fontSize: 26, fontWeight: 800 }}>Plata a reușit!</h1>
        <p style={{ margin: '10px 0 0', fontSize: 14, color: 'var(--muted)' }}>
          <strong style={{ color: 'var(--ink)' }}>{paidOrder.clips} clipuri {counterLabel(paidOrder.counter)}</strong> au intrat în sold. Factura e pe drum spre email.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 22, flexWrap: 'wrap' }}>
          <Link to="/creeaza" className="btn btn-primary">Creează primul clip din pachet</Link>
          <button className="btn btn-ghost" onClick={() => setPaidOrder(null)}>← Mai cumpără un pachet</button>
        </div>
      </div>
    );
  }

  // ---- Ecran de checkout (mock dev) ----
  if (checkout) {
    return (
      <div style={{ maxWidth: 460, margin: '0 auto', textAlign: 'center', paddingTop: 30 }}>
        <Spinner size={26} />
        <h1 className="bricolage" style={{ fontSize: 22, fontWeight: 800, marginTop: 14 }}>Te redirecționăm către Stripe…</h1>
        <p style={{ margin: '10px 0 20px', fontSize: 13.5, color: 'var(--muted)' }}>
          Plată securizată prin Stripe · card, Apple Pay, Google Pay. Total {formatRon(checkout.order.totalCents)}.
        </p>
        <div className="card" style={{ padding: 18, borderStyle: 'dashed' }}>
          <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--muted)' }}>
            Mediu de dezvoltare — plata Stripe este simulată.
          </p>
          <button className="btn btn-primary" data-testid="simulate-payment" onClick={() => payMut.mutate(checkout.order.id)} disabled={payMut.isPending} style={{ width: '100%' }}>
            {payMut.isPending ? <Spinner /> : `Simulează plata (${formatRon(checkout.order.totalCents)})`}
          </button>
        </div>
        <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => setCheckout(null)}>Anulează</button>
      </div>
    );
  }

  const subtotal = selected?.priceCents ?? 0;
  const discount = promoResult?.valid ? promoResult.discountCents ?? 0 : 0;
  const total = Math.max(0, subtotal - discount);

  return (
    <div>
      <h1 className="bricolage" style={{ fontWeight: 800, fontSize: 'clamp(24px,3vw,30px)' }}>Cumpără clipuri</h1>
      <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--muted)' }}>
        Clipurile intră în sold imediat după plată și <strong style={{ color: 'var(--ink)' }}>nu expiră niciodată</strong>. Prețuri finale, cu TVA inclus.
      </p>

      <div style={{ display: 'flex', gap: 12, margin: '20px 0', flexWrap: 'wrap' }}>
        <Segmented value={category} onChange={(v) => setCategory(v)} options={[{ value: 'organic', label: 'Organic' }, { value: 'reclame', label: 'Reclame' }]} />
        <Segmented value={tier} onChange={(v) => setTier(v)} options={[{ value: 'premium', label: 'Premium (~10 min)' }, { value: 'ultra', label: 'Ultra (editor)' }]} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 24, alignItems: 'start' }} className="dash-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="package-list">
          {catalog.isLoading && <p style={{ color: 'var(--muted)' }}>Se încarcă pachetele…</p>}
          {items.map((sku) => (
            <PackageCard key={sku.id} sku={sku} selected={selectedSku === sku.id} onSelect={() => { setSelectedSku(sku.id); setPromoResult(null); }} />
          ))}
          <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--muted)', borderStyle: 'dashed' }}>
            Peste 30 de clipuri pe lună sau pachet mixt? <a href="/suport" style={{ fontWeight: 800 }}>Scrie-ne</a> — pregătim ofertă dedicată.
          </div>
        </div>

        <aside className="card" style={{ padding: 18, position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 className="bricolage" style={{ fontSize: 18, fontWeight: 800 }}>Comanda ta</h2>
          {selected ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
                <span>{selected.clips} clipuri {categoryLabel(selected.category)} · {tierLabel(selected.tier)}</span>
                <span className="tnum">{formatRon(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: 'var(--ok)' }}>
                  <span>Reducere</span>
                  <span className="tnum">−{formatRon(discount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--muted)' }}>
                <span>TVA 19%</span>
                <span>inclus</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="input" placeholder="Cod promo" value={promo} onChange={(e) => setPromo(e.target.value)} style={{ padding: '10px 12px' }} />
                <button className="btn btn-ghost btn-sm" onClick={() => promoMut.mutate()} disabled={!promo.trim() || promoMut.isPending}>
                  {promoMut.isPending ? <Spinner /> : 'Aplică'}
                </button>
              </div>
              <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontWeight: 800 }}>Total de plată</span>
                <span className="bricolage tnum" data-testid="order-total" style={{ fontWeight: 800, fontSize: 22 }}>{formatRon(total)}</span>
              </div>

              <BillingProfiles profiles={billing.data?.items ?? []} selectedId={billingProfileId} onSelect={setBillingProfileId} />

              <button className="btn btn-primary" data-testid="checkout" onClick={() => orderMut.mutate()} disabled={orderMut.isPending || !selectedSku} style={{ width: '100%' }}>
                {orderMut.isPending ? <Spinner /> : 'Continuă la plată'}
              </button>
              <p style={{ margin: 0, fontSize: 11.5, color: 'var(--muted)', textAlign: 'center' }}>Plată securizată prin Stripe · card, Apple Pay, Google Pay</p>
            </>
          ) : (
            <p style={{ color: 'var(--muted)', fontSize: 13 }}>Alege un pachet pentru a continua.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function PackageCard({ sku, selected, onSelect }: { sku: CatalogSku; selected: boolean; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      data-testid="package-card"
      className="card"
      style={{ textAlign: 'left', padding: 18, display: 'flex', alignItems: 'center', gap: 16, borderColor: selected ? 'var(--accent)' : 'var(--line)', borderWidth: selected ? 2 : 1 }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="bricolage" style={{ fontSize: 20, fontWeight: 800 }}>{sku.clips} clipuri</span>
          {sku.recommended && <span className="pill" style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>★ Cel mai ales</span>}
          {sku.badge && !sku.recommended && <span className="pill" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>{sku.badge}</span>}
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
          {formatRon(sku.pricePerClipCents)} / clip · scenariu, voce, subtitrări, muzică, 2 regenerări incluse
        </p>
        {sku.discountActive && sku.countdownSeconds != null && (
          <span className="pill tnum" style={{ marginTop: 8, background: 'var(--warn-soft)', color: 'var(--warn)' }}>
            −{sku.discountPct}% · expiră în {formatCountdown(sku.countdownSeconds)}
          </span>
        )}
      </div>
      <div style={{ textAlign: 'right', flex: 'none' }}>
        {sku.oldPriceCents && sku.oldPriceCents > sku.priceCents && (
          <span className="tnum" style={{ display: 'block', fontSize: 12.5, color: 'var(--muted)', textDecoration: 'line-through' }}>{formatRon(sku.oldPriceCents)}</span>
        )}
        <span className="bricolage tnum" style={{ fontSize: 22, fontWeight: 800 }}>{formatRon(sku.priceCents)}</span>
      </div>
    </button>
  );
}
